import { zodResolver } from '@hookform/resolvers/zod';
import { Link, createFileRoute } from '@tanstack/react-router';
import { ArrowLeft, ImagePlus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { ErrorBanner } from '@/components/ErrorBanner';
import { Field, fieldAria } from '@/components/Field';
import { Input } from '@/components/Input';
import { PageHeader } from '@/components/PageHeader';
import { Textarea } from '@/components/exercise/Textarea';
import { Avatar } from '@/components/community/Avatar';
import { describeError, hasErrorCode } from '@/lib/api';
import { applyFieldErrors, explainError } from '@/lib/errors';
import { SAVED, showToast } from '@/lib/toast';
import { useAuth } from '@/lib/auth';
import { useUploadMedia } from '@/lib/queries/posts';
import { useUpdateMe } from '@/lib/queries/users';

/** Lo que acepta el servidor para un avatar. */
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_BIO = 300;

/** Mismas reglas que el alta de cuenta, más la bio del perfil público. */
const profileFormSchema = z.object({
  displayName: z.string().min(1).max(60),
  handle: z.string().regex(/^[a-z0-9_]{3,30}$/, 'sólo minúsculas, dígitos y guion bajo (3-30)'),
  bio: z.string().max(MAX_BIO),
});

type ProfileFormValues = z.infer<typeof profileFormSchema>;

export const Route = createFileRoute('/_app/settings/profile')({
  component: PublicProfileSettingsPage,
});

/**
 * Perfil público: lo que ven los demás.
 *
 * Es una pantalla distinta de la de ajustes porque lo que se edita aquí
 * —nombre, handle, foto, presentación y visibilidad— sale hacia fuera, mientras
 * que el perfil de `/settings` (sexo, altura, objetivo) sólo alimenta los
 * cálculos y no lo ve nadie más.
 */
function PublicProfileSettingsPage() {
  const { user, setUser } = useAuth();
  const update = useUpdateMe();
  const upload = useUploadMedia();
  const fileInput = useRef<HTMLInputElement>(null);

  const [avatarUrl, setAvatarUrl] = useState<string | null>(user?.avatarUrl ?? null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isPublic, setIsPublic] = useState(user?.isPublic ?? false);
  const [imageError, setImageError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(profileFormSchema),
    defaultValues: {
      displayName: user?.displayName ?? '',
      handle: user?.handle ?? '',
      bio: user?.bio ?? '',
    },
  });

  useEffect(() => {
    return () => {
      if (preview !== null) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  function pickAvatar(file: File | undefined): void {
    setImageError(null);
    if (file === undefined) return;
    if (!IMAGE_TYPES.includes(file.type)) {
      setImageError('Sólo admitimos imágenes JPEG, PNG o WebP.');
      return;
    }
    if (file.size > MAX_BYTES) {
      setImageError('La imagen pesa más de 5 MB. Prueba con una más pequeña.');
      return;
    }
    if (preview !== null) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    upload.mutate({ file, purpose: 'avatar' }, { onSuccess: (asset) => setAvatarUrl(asset.url) });
  }

  const onSubmit = handleSubmit((values: ProfileFormValues) => {
    const bio = values.bio.trim();
    update.mutate(
      {
        displayName: values.displayName.trim(),
        handle: values.handle.trim().toLowerCase(),
        bio: bio === '' ? null : bio,
        avatarUrl,
        isPublic,
      },
      {
        onSuccess: (updated) => {
          setUser(updated);
          reset({
            displayName: updated.displayName,
            handle: updated.handle,
            bio: updated.bio ?? '',
          });
          if (preview !== null) URL.revokeObjectURL(preview);
          setPreview(null);
          showToast(SAVED.publicProfileSaved);
        },
        // Un nombre de usuario cogido o un dato que el servidor rechaza se
        // marcan en su campo.
        onError: (error) => {
          applyFieldErrors(
            explainError(error),
            ['displayName', 'handle', 'bio'] as const,
            setError,
          );
        },
      },
    );
  });

  const dirty =
    isDirty || isPublic !== (user?.isPublic ?? false) || avatarUrl !== (user?.avatarUrl ?? null);
  const uploadError = upload.isError
    ? hasErrorCode(upload.error, 'payload_too_large')
      ? 'La imagen pesa demasiado para el servidor (máximo 5 MB).'
      : describeError(upload.error)
    : null;

  return (
    <>
      <PageHeader
        title="Perfil público"
        subtitle="Lo que ve el resto de la comunidad."
        action={
          <Link
            to="/settings"
            aria-label="Volver a ajustes"
            className="flex size-11 items-center justify-center rounded-xl border border-line text-ink-muted transition-colors active:bg-surface-raised"
          >
            <ArrowLeft className="size-5" aria-hidden="true" />
          </Link>
        }
      />

      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <Card title="Foto" description="Cuadrada se ve mejor: no la recortamos por ti.">
          <div className="flex items-center gap-4">
            <Avatar url={preview ?? avatarUrl} name={user?.displayName ?? ''} size="lg" />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <input
                ref={fileInput}
                type="file"
                accept={IMAGE_TYPES.join(',')}
                className="hidden"
                onChange={(event) => pickAvatar(event.target.files?.[0])}
              />
              <Button
                variant="secondary"
                loading={upload.isPending}
                icon={<ImagePlus className="size-4" />}
                onClick={() => fileInput.current?.click()}
              >
                Cambiar foto
              </Button>
              {avatarUrl === null && preview === null ? null : (
                <Button
                  variant="ghost"
                  icon={<Trash2 className="size-4" />}
                  onClick={() => {
                    if (preview !== null) URL.revokeObjectURL(preview);
                    setPreview(null);
                    setAvatarUrl(null);
                    upload.reset();
                    if (fileInput.current !== null) fileInput.current.value = '';
                  }}
                >
                  Quitar foto
                </Button>
              )}
            </div>
          </div>
          {imageError !== null || uploadError !== null ? (
            <p role="alert" className="mt-3 text-sm text-danger">
              {imageError ?? uploadError}
            </p>
          ) : null}
        </Card>

        <Card title="Tus datos">
          <div className="flex flex-col gap-4">
            <Field htmlFor="displayName" label="Nombre visible" error={errors.displayName?.message}>
              <Input
                {...fieldAria('displayName', errors.displayName?.message)}
                autoComplete="name"
                maxLength={60}
                {...register('displayName')}
              />
            </Field>

            <Field
              htmlFor="handle"
              label="Nombre de usuario"
              error={errors.handle?.message}
              hint="Es tu @: minúsculas, dígitos y guion bajo."
            >
              <Input
                {...fieldAria('handle', errors.handle?.message, true)}
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={30}
                {...register('handle')}
              />
            </Field>

            <Field
              htmlFor="bio"
              label="Presentación"
              error={errors.bio?.message}
              hint={`Como mucho ${MAX_BIO} caracteres.`}
            >
              <Textarea
                {...fieldAria('bio', errors.bio?.message, true)}
                rows={3}
                maxLength={MAX_BIO}
                placeholder="Qué entrenas, dónde, qué buscas…"
                {...register('bio')}
              />
            </Field>
          </div>
        </Card>

        <Card title="Visibilidad">
          <label className="flex min-h-11 cursor-pointer items-start gap-3">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={(event) => setIsPublic(event.target.checked)}
              className="mt-1 size-5 shrink-0 accent-[var(--color-brand)]"
            />
            <span className="min-w-0">
              <span className="block text-sm font-medium text-ink">Perfil público</span>
              <span className="mt-1 block text-sm text-ink-muted">
                Con el perfil público cualquiera puede encontrarte por tu nombre o tu @, ver tu
                presentación, tus publicaciones y las rutinas y recetas que hayas marcado como
                públicas. Con el perfil privado sólo se ven tu nombre y tu @, y tus publicaciones no
                salen en los muros.
              </span>
              <span className="mt-1 block text-xs text-ink-faint">
                Tu correo, tu diario de comidas y tus entrenamientos no se comparten nunca.
              </span>
            </span>
          </label>
        </Card>

        {update.isError && Object.keys(explainError(update.error).fieldErrors).length === 0 ? (
          <ErrorBanner
            title="No se ha podido guardar"
            error={update.error}
            errorOptions={{ saving: true }}
          />
        ) : null}

        <div className="flex items-center gap-3">
          <Button type="submit" size="lg" loading={update.isPending} disabled={!dirty}>
            Guardar perfil
          </Button>
        </div>
      </form>
    </>
  );
}
